// DEM-2129 — Loops C · UI → schedule_rrule (MUST_FIX #2) + preview de 5
// ocorrências client-side (MUST_FIX #1, fallback quando POST /v1/loops/preview
// não existir na API consumida).
//
// Este módulo é PURO e determinístico — é a fonte da verdade testável do
// mapeamento UI→RRULE e do cálculo do preview. A UI (LoopWizard) apenas coleta
// os campos e delega o `buildSchedule()` daqui; os testes (loops-rrule.test.ts)
// cravam cada modo/opção → payload esperado.
//
// Contrato com Loops A (backend src/loops/types.ts + rrule.ts):
//   - POST /v1/loops recebe { schedule_rrule: string, schedule_display: object }
//   - o backend valida via lib `rrule` (rrulestr) e impõe piso HARD de 60s
//   - "primeiro dia útil do mês" NÃO usa extensão server-side: é RRULE puro
//     FREQ=MONTHLY;BYDAY=MO,TU,WE,TH,FR;BYSETPOS=1 (o backend rrule.ts já cobre
//     BYSETPOS+BYDAY — confirmado em src/loops/rrule.ts:6). Assim o wizard NUNCA
//     envia RRULE que o backend não aceite.

import { rrulestr } from 'rrule';

/** Piso HARD de 60s (D1) — espelha LOOP_MIN_INTERVAL_SECONDS do backend. */
export const LOOP_MIN_INTERVAL_SECONDS = 60;

/** Quantas ocorrências o preview/guardrail anti-runaway exibe. */
export const PREVIEW_OCCURRENCE_COUNT = 5;

// ─── Modelo dos 3 modos de periodicidade (UI) ───────────────────────────────

export type SimpleFreqUnit = 'seconds' | 'minutes' | 'hours' | 'days';

/** Modo 1 — Frequência simples: "a cada X <unidade>" (mínimo 60s). */
export interface SimpleSchedule {
  mode: 'simple';
  every: number;
  unit: SimpleFreqUnit;
}

export type CalendarFreq = 'daily' | 'weekly' | 'monthly' | 'yearly';

/**
 * Modo 2 — Calendário: diária/semanal/mensal/anual selecionando hora(s) do dia,
 * dia(s) da semana (0=SU..6=SA no padrão rrule.js weekday), dia(s) do mês e
 * dia(s) do ano (1..366).
 */
export interface CalendarSchedule {
  mode: 'calendar';
  freq: CalendarFreq;
  /** Horas do dia 0..23 (BYHOUR). Minutos fixos em :00 (BYMINUTE=0). */
  byHour: number[];
  /** Dias da semana 0(SU)..6(SA) (BYDAY) — usado em weekly. */
  byWeekday: number[];
  /** Dias do mês 1..31 (BYMONTHDAY) — usado em monthly. */
  byMonthday: number[];
  /** Dias do ano 1..366 (BYYEARDAY) — usado em yearly. */
  byYearday: number[];
}

export type CustomKind =
  /** "dia X a cada N meses" → FREQ=MONTHLY;INTERVAL=N;BYMONTHDAY=X */
  | 'day_x_every_n_months'
  /** "a cada N dias" → FREQ=DAILY;INTERVAL=N */
  | 'every_n_days'
  /** "todo primeiro dia útil do mês" → FREQ=MONTHLY;BYDAY=MO..FR;BYSETPOS=1 */
  | 'first_business_day';

/** Modo 3 — Personalizada. */
export interface CustomSchedule {
  mode: 'custom';
  kind: CustomKind;
  /** intervalo N (para day_x_every_n_months / every_n_days). */
  interval?: number;
  /** dia do mês X (para day_x_every_n_months). */
  monthday?: number;
  /** hora do dia 0..23 (opcional, BYHOUR). */
  hour?: number;
}

export type LoopSchedule = SimpleSchedule | CalendarSchedule | CustomSchedule;

// rrule.js weekday index (0=SU..6=SA) → token RRULE.
const WEEKDAY_TOKEN = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;

function assertPositiveInt(n: number, field: string): void {
  if (!Number.isInteger(n) || n <= 0) {
    throw new Error(`${field} deve ser inteiro positivo (recebido: ${n})`);
  }
}

// ─── MUST_FIX #2 — mapeamento UI → RRULE ─────────────────────────────────────

/**
 * Converte a Frequência simples para segundos totais — usado pra validar o
 * piso de 60s ANTES de montar a RRULE (guardrail espelhando o backend).
 */
export function simpleIntervalSeconds(s: SimpleSchedule): number {
  const perUnit: Record<SimpleFreqUnit, number> = {
    seconds: 1,
    minutes: 60,
    hours: 3600,
    days: 86400,
  };
  return s.every * perUnit[s.unit];
}

/**
 * (a) Frequência simples → FREQ=SECONDLY|MINUTELY|HOURLY|DAILY + INTERVAL.
 * Normaliza pra maior unidade exata possível (120s → MINUTELY;INTERVAL=2) e
 * impõe o piso de 60s (rejeita intervalos menores).
 */
function buildSimpleRRule(s: SimpleSchedule): string {
  assertPositiveInt(s.every, 'every');
  const totalSeconds = simpleIntervalSeconds(s);
  if (totalSeconds < LOOP_MIN_INTERVAL_SECONDS) {
    throw new Error(
      `intervalo de ${totalSeconds}s abaixo do piso HARD de ${LOOP_MIN_INTERVAL_SECONDS}s`,
    );
  }
  switch (s.unit) {
    case 'seconds': {
      // ≥60s garantido acima. Normaliza pra MINUTELY quando múltiplo exato de 60.
      if (totalSeconds % 60 === 0) return `FREQ=MINUTELY;INTERVAL=${totalSeconds / 60}`;
      return `FREQ=SECONDLY;INTERVAL=${s.every}`;
    }
    case 'minutes':
      return `FREQ=MINUTELY;INTERVAL=${s.every}`;
    case 'hours':
      return `FREQ=HOURLY;INTERVAL=${s.every}`;
    case 'days':
      return `FREQ=DAILY;INTERVAL=${s.every}`;
  }
}

/**
 * (b) Calendário → FREQ=DAILY|WEEKLY|MONTHLY|YEARLY + BYHOUR/BYDAY/BYMONTHDAY/BYYEARDAY.
 */
function buildCalendarRRule(s: CalendarSchedule): string {
  const parts: string[] = [];
  const freqToken: Record<CalendarFreq, string> = {
    daily: 'DAILY',
    weekly: 'WEEKLY',
    monthly: 'MONTHLY',
    yearly: 'YEARLY',
  };
  parts.push(`FREQ=${freqToken[s.freq]}`);

  const hours = [...new Set(s.byHour)].filter((h) => h >= 0 && h <= 23).sort((a, b) => a - b);
  if (hours.length > 0) {
    parts.push(`BYHOUR=${hours.join(',')}`);
    parts.push('BYMINUTE=0');
  }

  if (s.freq === 'weekly') {
    const days = [...new Set(s.byWeekday)]
      .filter((d) => d >= 0 && d <= 6)
      .sort((a, b) => a - b)
      .map((d) => WEEKDAY_TOKEN[d]);
    if (days.length === 0) throw new Error('calendário semanal exige ao menos 1 dia da semana');
    parts.push(`BYDAY=${days.join(',')}`);
  }

  if (s.freq === 'monthly') {
    const md = [...new Set(s.byMonthday)]
      .filter((d) => d >= 1 && d <= 31)
      .sort((a, b) => a - b);
    if (md.length === 0) throw new Error('calendário mensal exige ao menos 1 dia do mês');
    parts.push(`BYMONTHDAY=${md.join(',')}`);
  }

  if (s.freq === 'yearly') {
    const yd = [...new Set(s.byYearday)]
      .filter((d) => d >= 1 && d <= 366)
      .sort((a, b) => a - b);
    if (yd.length === 0) throw new Error('calendário anual exige ao menos 1 dia do ano');
    parts.push(`BYYEARDAY=${yd.join(',')}`);
  }

  return parts.join(';');
}

/**
 * (c) Personalizada → RRULE quando expressável. "primeiro dia útil do mês" NÃO
 * usa extensão server-side: mapeia pra RRULE puro BYSETPOS+BYDAY, que o backend
 * Loops A aceita (rrule.ts cobre BYSETPOS). O wizard nunca envia RRULE inválida.
 */
function buildCustomRRule(s: CustomSchedule): string {
  const parts: string[] = [];
  switch (s.kind) {
    case 'day_x_every_n_months': {
      assertPositiveInt(s.interval ?? 0, 'interval');
      const day = s.monthday ?? 0;
      if (!Number.isInteger(day) || day < 1 || day > 31) {
        throw new Error(`monthday deve estar entre 1 e 31 (recebido: ${day})`);
      }
      parts.push('FREQ=MONTHLY', `INTERVAL=${s.interval}`, `BYMONTHDAY=${day}`);
      break;
    }
    case 'every_n_days': {
      assertPositiveInt(s.interval ?? 0, 'interval');
      parts.push('FREQ=DAILY', `INTERVAL=${s.interval}`);
      break;
    }
    case 'first_business_day': {
      // Primeiro dia útil (seg–sex) de cada mês: RRULE puro.
      parts.push('FREQ=MONTHLY', 'BYDAY=MO,TU,WE,TH,FR', 'BYSETPOS=1');
      break;
    }
  }
  if (s.hour !== undefined && s.hour >= 0 && s.hour <= 23) {
    parts.push(`BYHOUR=${s.hour}`, 'BYMINUTE=0');
  }
  return parts.join(';');
}

/** Monta a string RRULE (RFC 5545, sem prefixo RRULE:) a partir do modelo UI. */
export function scheduleToRRule(schedule: LoopSchedule): string {
  switch (schedule.mode) {
    case 'simple':
      return buildSimpleRRule(schedule);
    case 'calendar':
      return buildCalendarRRule(schedule);
    case 'custom':
      return buildCustomRRule(schedule);
  }
}

/**
 * Descrição humana curta do schedule — vai em `schedule_display.human` no
 * payload e é usada na Lista (periodicidade humanizada).
 */
export function humanizeSchedule(schedule: LoopSchedule): string {
  switch (schedule.mode) {
    case 'simple': {
      const unitLabel: Record<SimpleFreqUnit, [string, string]> = {
        seconds: ['segundo', 'segundos'],
        minutes: ['minuto', 'minutos'],
        hours: ['hora', 'horas'],
        days: ['dia', 'dias'],
      };
      const [sing, plur] = unitLabel[schedule.unit];
      return schedule.every === 1 ? `a cada ${sing}` : `a cada ${schedule.every} ${plur}`;
    }
    case 'calendar': {
      const freqLabel: Record<CalendarFreq, string> = {
        daily: 'diariamente',
        weekly: 'semanalmente',
        monthly: 'mensalmente',
        yearly: 'anualmente',
      };
      return freqLabel[schedule.freq];
    }
    case 'custom': {
      switch (schedule.kind) {
        case 'day_x_every_n_months':
          return `todo dia ${schedule.monthday} a cada ${schedule.interval} ${
            schedule.interval === 1 ? 'mês' : 'meses'
          }`;
        case 'every_n_days':
          return `a cada ${schedule.interval} ${schedule.interval === 1 ? 'dia' : 'dias'}`;
        case 'first_business_day':
          return 'todo primeiro dia útil do mês';
      }
    }
  }
}

/**
 * Payload de schedule enviado ao POST /v1/loops (subset). O wizard combina isto
 * com name/execution_type/... antes de POST.
 */
export interface SchedulePayload {
  schedule_rrule: string;
  schedule_display: {
    human: string;
    /** Modelo UI serializado — permite re-hidratar o wizard na edição. */
    ui: LoopSchedule;
  };
}

/** Monta o par (schedule_rrule, schedule_display) canônico enviado à API. */
export function buildSchedulePayload(schedule: LoopSchedule): SchedulePayload {
  return {
    schedule_rrule: withTzidDtstart(scheduleToRRule(schedule)),
    schedule_display: {
      human: humanizeSchedule(schedule),
      ui: schedule,
    },
  };
}

// ─── DEM-2124 · timezone-awareness (TZID) — espelho do backend src/loops/rrule.ts ──
// GOTCHA da lib `rrule` (provado no backend, #1181): com DTSTART;TZID a lib devolve
// ocorrências em "fake UTC" — o wall-clock local disfarçado de UTC — e o resultado
// depende do TZ do AMBIENTE (no browser, do SO do usuário). A lib NUNCA vê TZID:
// normalizamos pro domínio fake-UTC e a conversão de fuso é toda nossa, na borda,
// com Intl nativo (DST-safe, zero dependência nova).

/** TZID declarado no bloco DTSTART da string RRULE, ou null (legado = UTC). */
export function extractTzid(rruleStr: string): string | null {
  const m = /TZID=([^:;\s]+)/.exec(rruleStr ?? '');
  return m ? m[1]! : null;
}

/** Wall-clock do instante `real` na timezone `tz`, montado como Date UTC (forma "fake UTC" da lib). */
export function utcToFakeUtc(real: Date, tz: string): Date {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(real);
  const get = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((p) => p.type === type)?.value ?? NaN);
  return new Date(Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second')));
}

/** Interpreta os componentes UTC de `fake` como wall-clock na timezone `tz` → instante real. */
export function fakeUtcToUtc(fake: Date, tz: string): Date {
  // offset(t) = wallClock(t) − t. real = fake − offset(real); itera 2x pra convergir
  // (o offset muda em transições de DST; 2 iterações cobrem os casos reais).
  let real = fake.getTime() - tzOffsetMs(fake, tz);
  real = fake.getTime() - tzOffsetMs(new Date(real), tz);
  return new Date(real);
}

/** Offset (ms) da timezone `tz` vs UTC no instante `at` (>0 = à frente de UTC). */
function tzOffsetMs(at: Date, tz: string): number {
  return utcToFakeUtc(at, tz).getTime() - at.getTime();
}

/**
 * DEM-2124 — pina o fuso do USUÁRIO no schedule: RRULE com BYHOUR ganha bloco
 * DTSTART;TZID=<fuso do navegador> pra "10:00" significar 10:00 no relógio de
 * quem criou (sem isso o backend interpreta UTC → dispara 3h adiantado no BR).
 * Intervalos puros (sem BYHOUR) não têm wall-clock — ficam como estão. Idempotente.
 */
export function withTzidDtstart(rrule: string, at: Date = new Date()): string {
  if (!/BYHOUR=/i.test(rrule) || /DTSTART/i.test(rrule)) return rrule;
  let tz: string | null = null;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    tz = null;
  }
  if (!tz) return rrule;
  const wall = utcToFakeUtc(at, tz);
  const pad = (n: number): string => String(n).padStart(2, '0');
  const stamp = `${wall.getUTCFullYear()}${pad(wall.getUTCMonth() + 1)}${pad(wall.getUTCDate())}T000000`;
  return `DTSTART;TZID=${tz}:${stamp}\nRRULE:${rrule}`;
}

// ─── MUST_FIX #1 — preview de 5 ocorrências (fallback client-side) ───────────

/**
 * Computa as próximas N ocorrências de uma RRULE client-side (fallback quando o
 * endpoint POST /v1/loops/preview não existe na API). Usa a MESMA lib `rrule`
 * do backend, então o resultado é consistente com o scheduler.
 *
 * @param rruleStr string RRULE (sem DTSTART) — a mesma enviada no POST.
 * @param startAt  âncora (dtstart). Se ausente/passado, usa agora.
 * @param count    quantas ocorrências (default 5 — guardrail anti-runaway).
 */
export function computeOccurrences(
  rruleStr: string,
  startAt?: Date | null,
  count: number = PREVIEW_OCCURRENCE_COUNT,
): Date[] {
  const now = new Date();
  const anchor = startAt && startAt.getTime() > now.getTime() ? startAt : now;
  const tzid = extractTzid(rruleStr);
  if (tzid) {
    // DEM-2124 — bloco DTSTART;TZID: normaliza pro domínio fake-UTC (a lib NUNCA
    // vê TZID) e converte na borda via Intl — mesma solução do backend (#1181).
    // Sem isto, BYHOUR=10 + TZID São Paulo renderizava 07:00 (bug real).
    const fakeStr = rruleStr.replace(/DTSTART;TZID=[^:]+:(\d{8}T\d{6})/i, 'DTSTART:$1Z');
    const rule = rrulestr(fakeStr); // âncora canônica = DTSTART próprio da string
    const occurrences: Date[] = [];
    let cursor: Date | null = new Date(utcToFakeUtc(anchor, tzid).getTime() - 1000);
    for (let i = 0; i < count; i++) {
      const next: Date | null = rule.after(cursor, false);
      if (!next) break;
      occurrences.push(fakeUtcToUtc(next, tzid));
      cursor = next;
    }
    return occurrences;
  }
  // Legado (sem TZID): semântica UTC inalterada.
  // rrulestr aceita "FREQ=..." puro; ancoramos com dtstart.
  const rule = rrulestr(rruleStr, { dtstart: anchor });
  const occurrences: Date[] = [];
  let cursor: Date | null = new Date(anchor.getTime() - 1000); // inclui a âncora se coincidir
  for (let i = 0; i < count; i++) {
    const next: Date | null = rule.after(cursor, false);
    if (!next) break;
    occurrences.push(next);
    cursor = next;
  }
  return occurrences;
}

/**
 * Fonte da estratégia usada no preview (útil pro teste/telemetria/UI).
 * 'backend' = POST /v1/loops/preview respondeu; 'client' = fallback rrule local.
 */
export type PreviewSource = 'backend' | 'client';

export interface PreviewResult {
  occurrences: Date[];
  source: PreviewSource;
}

/**
 * MUST_FIX #1 — resolve o preview de N ocorrências: TENTA o endpoint backend
 * POST /v1/loops/preview; se indisponível (endpoint ausente / erro), DEGRADA
 * pro cálculo client-side (computeOccurrences). Sempre retorna as datas + a
 * fonte usada. O caller (wizard) só libera "salvar" quando isto resolveu.
 */
export async function resolvePreview(
  rruleStr: string,
  startAt: Date | null,
  fetchBackend: (payload: {
    schedule_rrule: string;
    start_at?: string | null;
    count?: number;
  }) => Promise<{ occurrences: string[] }>,
  count: number = PREVIEW_OCCURRENCE_COUNT,
): Promise<PreviewResult> {
  try {
    const res = await fetchBackend({
      schedule_rrule: rruleStr,
      start_at: startAt ? startAt.toISOString() : null,
      count,
    });
    const dates = (res.occurrences ?? [])
      .map((iso) => new Date(iso))
      .filter((d) => !Number.isNaN(d.getTime()))
      .slice(0, count);
    if (dates.length > 0) return { occurrences: dates, source: 'backend' };
    // endpoint respondeu vazio → cai no client-side por segurança.
    return { occurrences: computeOccurrences(rruleStr, startAt, count), source: 'client' };
  } catch {
    // 404/501/ausência do endpoint /preview → fallback client-side (lib rrule).
    return { occurrences: computeOccurrences(rruleStr, startAt, count), source: 'client' };
  }
}
